<template>
    <div
        v-on-clickaway="hideDropdown"
        :class="['select-box', styleClass, { disabled: isDisabled, opened: visible }]"
    >
        <div
            :class="['selectbox-item-wrapper', { tooltiperror: invalid }]"
            @click.prevent="toggleVisibility($event)"
        >
            <div class="selected-item">
                <span
                    v-if="selectedOption && selectedOption.hasOwnProperty('color')"
                    :class="['label-icon', 'color-' + selectedOption.color]"
                >
                </span>
                <img v-if="icon" :src="icon" alt="" width="24" height="24" />
                <span
                    class="label-selected"
                    :title="titleText"
                    :class="{ 'selected-as-placeholder': placeholderText === placeholder }"
                >
                    {{ placeholderText }}
                </span>
                <span v-if="clarification" :title="clarification()" class="select-box__clarification">
                    {{ clarification() }}
                </span>
            </div>
            <div class="label-place-holder">{{ placeholder }}</div>
            <div class="select-box-arrow"><i class="icon-chewron-down"></i></div>
        </div>
        <div v-show="visible" :class="['select-box-list', { 'list-open-up': openUp || listToTop }]">
            <label v-if="search" :for="`forAT_search-${placeholder}`" class="label-for-screen-reader">
                {{ $t('common.searchPlaceholder') }}
            </label>
            <input
                v-if="search"
                :id="`forAT_search-${placeholder}`"
                ref="search"
                v-model="selectKeyword"
                type="text"
                :placeholder="$t('common.searchPlaceholder')"
                @keyup.down="pointerForward()"
                @keyup.up="pointerBackward()"
                @keydown.enter.stop.prevent.self="addPointerElement()"
            />
            <Simplebar class="with-border-track" :style="[selectBoxListStyles]">
                <ul ref="list" class="select-box__list">
                    <li
                        v-for="(row, index) in filteredOptions"
                        :key="index"
                        :class="{ highlight: index == pointer, active: value === row.value }"
                        @click.prevent="selectItem(row)"
                        @mouseenter="pointer = index"
                    >
                        <span
                            v-if="row.hasOwnProperty('icon') || row.hasOwnProperty('color')"
                            :class="[
                                'item-icon',
                                {
                                    [`color-${row.color}`]: row.hasOwnProperty('color'),
                                },
                            ]"
                        >
                            <img
                                v-if="row.hasOwnProperty('icon')"
                                :src="row.icon"
                                alt=""
                                width="24"
                                height="24"
                            />
                        </span>
                        <div class="item-name" :title="row.title ? row.title : row.name">
                            {{ row.name }}
                        </div>
                        <div v-if="row.hasOwnProperty('commentId')" class="item-comment pull-right">
                            {{ $t(row.commentId) }}
                        </div>
                    </li>
                    <li v-show="filteredOptions.length === 0 && selectKeyword">
                        <span>
                            <slot name="noResult">{{ $t('common.noSearchResults') }}</slot>
                        </span>
                    </li>
                </ul>
            </Simplebar>
        </div>
    </div>
</template>

<script type="text/babel">
import { mixin as clickaway } from 'vue-clickaway';
import { DEFAULT_OPTION_HEIGHT_DDLIST, MAX_ELEMENTS_DDLIST } from '../../constants';
import Simplebar from 'simplebar-vue';

export default {
    components: {
        Simplebar,
    },

    mixins: [clickaway],

    props: {
        placeholder: {
            type: String,
            default: '',
        },
        value: {
            validator: prop => ['number', 'string', 'object'].includes(typeof prop),
            required: true,
        },
        options: {
            type: Array,
            required: true,
        },
        styleClass: {
            type: String,
            default: '',
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        search: {
            type: Boolean,
            default: true,
        },
        openUp: {
            type: Boolean,
            default: false,
        },
        headerHeight: {
            type: Number,
            default: 0,
        },
        unsavedCallback: {
            type: Function,
            required: false,
            default: () => {
                return Promise.resolve();
            },
        },
    },

    data() {
        return {
            visible: false,
            placeholderText: this.placeholder,
            titleText: this.placeholder,
            icon: null,
            clarification: null,
            pointer: 0,
            selectKeyword: '',
            selectedOption: {},
            listToTop: false,
            topHeight: 120,
            itemHeightInSelectBox: DEFAULT_OPTION_HEIGHT_DDLIST,
            numberItemsInSelectBox: MAX_ELEMENTS_DDLIST,
            maxItemsInSelectBox: MAX_ELEMENTS_DDLIST,
            searchHeight: null,
        };
    },

    computed: {
        filteredOptions() {
            return this.selectKeyword.trim() === ''
                ? this.options
                : this.options.filter(
                      item => item.name.toLowerCase().indexOf(this.selectKeyword.trim().toLowerCase()) > -1
                  );
        },
        selectBoxListStyles() {
            return {
                'max-height': this.selectBoxHeight + 'px',
                'margin-top': this.searchHeight + 'px',
            };
        },

        selectBoxHeight() {
            let itemCounter =
                this.selectKeyword.trim() !== ''
                    ? Math.min(this.filteredOptions.length, this.numberItemsInSelectBox)
                    : this.numberItemsInSelectBox;
            if (this.search) {
                return this.itemHeightInSelectBox * itemCounter + this.searchHeight;
            }
            return this.itemHeightInSelectBox * itemCounter;
        },
    },

    watch: {
        '$route.path'() {
            // Hide drop down when going back/forward in history
            this.hideDropdown();
        },

        value(v) {
            this.updatePlaceholderByValue(v);
        },

        options() {
            this.updatePlaceholderByValue(this.value);
        },

        selectKeyword() {
            this.pointer = 0;
        },
    },

    created() {
        this.updatePlaceholderByValue(this.value);
        this.titleText =
            this.selectedOption && ![null, undefined].includes(this.selectedOption.title)
                ? this.selectedOption.title
                : this.placeholderText;
    },

    methods: {
        selectItem(item) {
            this.unsavedCallback()
                .then(() => {
                    this.$emit('input', item.value);
                    this.placeholderText = item.value !== null ? item.name : this.placeholder;
                    this.visible = false;
                })
                .catch(() => {});
        },

        /**
         * Moves selection pointer in  dropdown list to the next line (if available)
         */
        pointerForward() {
            if (this.pointer < this.filteredOptions.length - 1) {
                this.pointer++;
                let pointerPosition = this.pointer * this.itemHeightInSelectBox;
                if (
                    this.$refs.list.scrollTop <=
                    pointerPosition - this.itemHeightInSelectBox * MAX_ELEMENTS_DDLIST
                ) {
                    this.$refs.list.scrollTop =
                        pointerPosition - this.itemHeightInSelectBox * (MAX_ELEMENTS_DDLIST - 1);
                }
            }
        },

        /**
         * Moves selection pointer in  dropdown list to the previous line (if available)
         */
        pointerBackward() {
            if (this.pointer > 0) {
                this.pointer--;
                let pointerPosition = this.pointer * this.itemHeightInSelectBox;
                if (this.$refs.list.scrollTop >= pointerPosition) {
                    this.$refs.list.scrollTop = pointerPosition;
                }
            }
        },

        /**
         * Selects pointed element
         */
        addPointerElement() {
            if (this.filteredOptions.length > 0) {
                this.selectItem(this.filteredOptions[this.pointer]);
            }
            this.pointer = 0;
        },

        updatePlaceholderByValue(v) {
            this.selectedOption =
                typeof v === 'number' || !!v ? this.options.find(option => option.value === v) : undefined;
            let selectedOption = this.selectedOption;
            this.placeholderText = selectedOption ? selectedOption.name : this.placeholder;
            this.titleText =
                this.selectedOption && ![null, undefined].includes(this.selectedOption.title)
                    ? selectedOption.title
                    : this.placeholderText;
            this.icon = selectedOption && selectedOption.hasOwnProperty('icon') ? selectedOption.icon : null;
            this.clarification =
                selectedOption && selectedOption.hasOwnProperty('clarification')
                    ? selectedOption.clarification
                    : null;
        },

        hideDropdown() {
            this.visible = false;
            this.listToTop = false;
        },

        toggleVisibility(e) {
            if (!this.isDisabled) {
                let me = e.target;
                while (!me.classList.contains('select-box') && me.parentNode) {
                    me = me.parentNode;
                }
                let box = me.getBoundingClientRect();
                let body = document.body;
                let html = document.documentElement;
                let documentHeight = Math.max(
                    body.scrollHeight,
                    body.offsetHeight,
                    html.clientHeight,
                    html.scrollHeight,
                    html.offsetHeight
                );
                this.listToTop =
                    box.bottom > documentHeight - (documentHeight - this.topHeight - this.headerHeight) / 2;
                this.visible = !this.visible;
                if (this.visible) {
                    this.$nextTick(() => {
                        if (
                            this.$refs.list &&
                            this.$refs.list.getElementsByTagName('li').length > 0 &&
                            this.$refs.list.getElementsByTagName('li')[0].offsetHeight > 0
                        ) {
                            this.itemHeightInSelectBox = this.$refs.list.getElementsByTagName(
                                'li'
                            )[0].offsetHeight;
                        } else {
                            this.itemHeightInSelectBox = DEFAULT_OPTION_HEIGHT_DDLIST;
                        }

                        if (this.search) {
                            if (this.$refs.search && this.$refs.search.offsetHeight > 0) {
                                this.searchHeight = this.$refs.search.offsetHeight;
                            } else {
                                this.searchHeight = DEFAULT_OPTION_HEIGHT_DDLIST;
                            }
                        }
                    });
                }
                if (!this.visible) {
                    this.listToTop = false;
                }
                let freeHeight = this.listToTop
                    ? box.top - this.topHeight - this.headerHeight
                    : documentHeight - box.bottom;
                let itemSearch = this.search ? 1 : 0;

                this.numberItemsInSelectBox =
                    Math.min(
                        Math.max(Math.floor(freeHeight / this.itemHeightInSelectBox), 2),
                        this.maxItemsInSelectBox
                    ) - itemSearch;

                if (this.visible && this.search) {
                    this.selectKeyword = '';
                    setTimeout(() => {
                        this.$refs.search.focus();
                    }, 1);
                }
            }
        },
    },
};
</script>
